import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import OrderDesign from './OrderDesign';
import OrderSidebarOrderList from './components/OrderSidebarOrderList';
import './components/OrderSidebarStyleCard.css';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from './hooks/useSoundEffects';
import MenuPaintedButton from './components/MenuPaintedButton';
import { BATTLE_UI_END_TURN_BUTTON_PATH } from './config/welcomeAssets';
import { getStrategyModalPanelStyle } from './utils/strategyModalPanelStyle';
import './StrategyModal.css';

function CompletedOrdersModal({
  show,
  onClose,
  completedOrders,
  gameState,
  userId = null,
  hoveredOrder,
  onHoverOrder,
  essiaLuhrActive = false,
  onEssiaLuhrSelect = null,
  isMobile = false,
}) {
  const { t } = useTranslation();
  const [hoveredOrderGroupKey, setHoveredOrderGroupKey] = useState(null);

  if (!show || !completedOrders) return null;

  const hoveredOrderData = hoveredOrder !== null && completedOrders.length > 0
    ? completedOrders.find((o, idx) => (o.instanceId || o.id || idx) === hoveredOrder)
    : null;

  const modalTree = (
    <div
      className={`strategy-modal-overlay${isMobile ? ' strategy-modal-overlay--mobile' : ''}`}
      style={getStrategyModalPanelStyle(isMobile)}
      data-no-battle-flashlight="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {hoveredOrderData && !isMobile && (
        <div
          style={{
            position: 'absolute',
            left: 'calc(50% + 300px)',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: '20px',
            zIndex: 15101,
            pointerEvents: 'none',
          }}
        >
          <OrderDesign
            order={hoveredOrderData}
            count={0}
            minimal={false}
            lazyLoad={false}
            gameState={gameState}
            userId={userId}
          />
        </div>
      )}
      <div
        className={`strategy-modal-panel${isMobile ? ' strategy-modal-panel--mobile' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="strategy-modal-panel__safe-area">
        <div className="strategy-modal-header">
          <h3 style={{ color: 'white', margin: 0 }}>
            {t('battleUI.completedOrders') || 'Completed Orders'}
          </h3>
          <MenuPaintedButton
            type="button"
            className="strategy-modal-close-btn"
            imagePath={BATTLE_UI_END_TURN_BUTTON_PATH}
            onClick={onClose}
          >
            {t('battleUI.close') || 'Close'}
          </MenuPaintedButton>
        </div>

        <div style={{ marginBottom: isMobile ? '6px' : '10px', color: '#aaa', fontSize: isMobile ? '0.8em' : '0.9em' }}>
          {t('battleUI.totalCompletedOrders') || 'Total'}: {completedOrders.length || 0}
        </div>

        <div className="strategy-modal-list">
          {completedOrders.length > 0 ? (
            <OrderSidebarOrderList
              items={completedOrders.filter((order) => !essiaLuhrActive || order.type === 'Strategem')}
              getGroupKey={(order) => `${order.id}|${order.type || 'unknown'}`}
              getDisplayName={(order) => order.name || 'Unknown Order'}
              getDisplayCost={(order) => order.cost ?? 'N/A'}
              costLabel={t('strategy.cost')}
              renderRowWrapperClassName={(order, group) => {
                const hoverKey = order.instanceId || order.id || group.firstIndex;
                const isHovered = hoveredOrder === hoverKey || hoveredOrderGroupKey === group.key;
                const isStrategem = order.type === 'Strategem';
                const showGetButton =
                  essiaLuhrActive && isStrategem && (isMobile || isHovered) && onEssiaLuhrSelect;
                const essiaHighlight = essiaLuhrActive && isStrategem;
                return `${essiaHighlight ? 'strategy-modal-order-entry--essia' : ''}${
                  showGetButton ? ' strategy-modal-order-entry--with-actions' : ''
                }`.trim();
              }}
              onItemMouseEnter={(order, group) => {
                if (!isMobile) {
                  playSoundEffect('/sounds/hoverover.mp3', 0.3);
                }
                onHoverOrder(order.instanceId || order.id || group.firstIndex);
                setHoveredOrderGroupKey(group.key);
              }}
              onItemMouseLeave={() => {
                onHoverOrder(null);
                setHoveredOrderGroupKey(null);
              }}
              renderAfterCard={(order, group) => {
                const hoverKey = order.instanceId || order.id || group.firstIndex;
                const isHovered = hoveredOrder === hoverKey || hoveredOrderGroupKey === group.key;
                const isStrategem = order.type === 'Strategem';
                const showGetButton =
                  essiaLuhrActive && isStrategem && (isMobile || isHovered) && onEssiaLuhrSelect;
                if (!showGetButton) return null;
                return (
                  <button
                    type="button"
                    className="strategy-modal-get-btn strategy-modal-get-btn--affordable"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEssiaLuhrSelect(order.instanceId || order.id, order.id);
                    }}
                  >
                    {t('battleUI.get') || 'Get'}
                  </button>
                );
              }}
              resolveOrderRecord={(order) => order}
            />
          ) : (
            <div style={{ color: '#aaa', textAlign: 'center', padding: '20px' }}>
              {essiaLuhrActive
                ? (t('battleUI.noStrategemsInCompletedOrders') || 'No strategems in completed orders')
                : (t('battleUI.noCompletedOrders') || 'No completed orders yet')}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return modalTree;
  }
  if (typeof document === 'undefined' || !document.body) {
    return modalTree;
  }
  return createPortal(modalTree, document.body);
}

export default CompletedOrdersModal;
